'use client';

/**
 * WebMCP bridge (ADR-0049, 2026-10-01): the non-visual component that offers
 * the open apps' commands to the browser's own assistant. Mounted once beside
 * `WorkspaceAgentCommandReceiver` in workspace-shell.tsx, inside the bus
 * provider, so it has the live `WidgetCommandBus`.
 *
 * WHAT IT DOES, AND ONLY THAT. The gate (section 5 of the ADR), the origin
 * trial meta, feature detection, the store selectors and the closures the
 * registrar needs. Every rule about tools lives in lib/workspaces/webmcp-tools.ts
 * and every registry behaviour in lib/workspaces/webmcp-registrar.ts, both
 * framework free and unit tested; this file is the wiring.
 *
 * THE GATE. `webmcpTools` in RuntimeConfig (`WEBMCP_TOOLS=1`; unset, on only
 * under the dev server), a signed-in viewer who is neither a guest nor an
 * anonymous read-only visitor, and the API present. With neither a token nor
 * the person's own Chrome flag there is no API and this renders nothing and
 * registers nothing. The API object is `document.modelContext` in Chrome 154
 * and `navigator.modelContext` in the draft spec; both are tried. When the
 * API is absent and the environment has an origin trial token, the token's
 * meta tag is appended to the head at runtime (the root layout is static, so
 * a per-environment value cannot be baked into it) and the API is polled for
 * a moment, because it can appear a tick after the meta lands.
 *
 * NO RE-RENDER PER FRAME. Every `NodeFrame` streams a store update per
 * animation frame during a drag. The app in front is derived by one
 * memoised selector over the store, the way `useTopAdWindowId` does it in
 * store.tsx: it recomputes once per store version and returns a string, so
 * this component re-renders only when a different window comes to the
 * front. The handset answer and the desktop answer are both carried in that
 * string, and the device gate (`!isMd || handsetLandscape`, the same test the
 * canvas and `NodeFrame` use) picks one in render, so a rotation needs no
 * store change to take effect.
 *
 * THE ACTOR. `kind: 'browser'`, one id per viewer, the translated assistant
 * name, and the viewer's username as `ownerName`, mirroring `actorFor` in
 * lib/mcp/workspace-widget-tools.ts. The presence layer labels any
 * actor-stamped command as an agent by its `name` and `ownerName` and never
 * branches on `kind` (public/widgets/shared/collab-kit.js, sdk.v2.js), so a
 * distinct kind costs nothing and keeps the browser apart from platform
 * agents (`agent`) and connectors (`mcp`) in the in-document log.
 *
 * THE CONSENT (ADR-0049 "Open before production"; owner, 2026-10-01). The
 * person is asked ONCE, by this page's dialog (WebMcpConsentDialog, mounted
 * right after this component in workspace-shell.tsx), the first time their
 * browser's assistant calls a tool; Allow is remembered per person through
 * GET/POST /api/workspaces/webmcp-consent. The page's copy of that record,
 * and every rule about how long it is trusted, is `WebMcpConsentGate`
 * (lib/workspaces/webmcp-consent-gate.ts). This file only wires it: one gate
 * per viewer, given the dialog; a read when the bridge becomes active, so a
 * call that comes soon does not wait for it; and a drop when Settings
 * announces a removal. The first build kept one read for the life of the
 * page, so a removal was not honoured by pages already open (S-382).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/components/auth-provider';
import { useT } from '@/components/i18n-provider';
import { useWorkspaceAnon } from '@/components/workspace/anon-context';
import type { CatalogListing } from '@/components/workspace/discover/catalog-card';
import { cachedJson } from '@/components/workspace/discover/discover-data';
import { useIsMd } from '@/components/workspace/panels/panel-shell';
import {
  useFullscreenItemId,
  useWorkspaceActions,
  useWorkspaceState,
  type WorkspaceStoreState,
} from '@/components/workspace/store';
import { useHandsetLandscape } from '@/hooks/use-handset-landscape';
import { isGuestUser } from '@/lib/auth/is-guest';
import { getRuntimeConfig } from '@/lib/runtime-config';
import type { WorkspaceItemDTO } from '@/lib/workspaces/types';
import { useWidgetCommandBus } from '@/lib/workspaces/widget-command-bus-context';
import {
  openWebMcpConsentChannel,
  readWebMcpConsentRecord,
  saveWebMcpConsentAllow,
  WebMcpConsentGate,
} from '@/lib/workspaces/webmcp-consent-gate';
import {
  pickModelContext,
  WebMcpRegistrar,
  type CatalogApp,
  type ModelContextLike,
} from '@/lib/workspaces/webmcp-registrar';
import { frontAppItemId, MAX_FIND_APPS } from '@/lib/workspaces/webmcp-tools';
import { askWebMcpConsent } from './webmcp-consent-dialog';

/** How long to wait for the API after the origin trial meta lands. */
const ORIGIN_TRIAL_POLL_MS = 100;
const ORIGIN_TRIAL_POLLS = 10;

type ModelContextHost = { modelContext?: unknown };

/** `document.modelContext` (Chrome 154) or `navigator.modelContext` (the draft spec), or null. */
function findModelContext(): ModelContextLike | null {
  if (typeof document === 'undefined' || typeof navigator === 'undefined') return null;
  return pickModelContext([
    (document as unknown as ModelContextHost).modelContext,
    (navigator as unknown as ModelContextHost).modelContext,
  ]);
}

/** Append the origin trial token's meta once; a second mount finds the first one. */
export function ensureOriginTrialMeta(doc: Document, token: string): void {
  const head = doc.head ?? doc.documentElement;
  if (!head) return;
  for (const el of Array.from(head.querySelectorAll('meta[http-equiv="origin-trial"]'))) {
    if (el.getAttribute('content') === token) return;
  }
  const meta = doc.createElement('meta');
  meta.setAttribute('http-equiv', 'origin-trial');
  meta.setAttribute('content', token);
  head.appendChild(meta);
}

/* ------------------------------------------------------------------ */
/* The app in front                                                   */
/* ------------------------------------------------------------------ */

/**
 * Apps are `content` windows; the registrar re-checks the answer against the
 * bus (an iframe app registered by the bridge), so a content window that is
 * not an app simply yields no `app.*` tools.
 */
const isAppWindow = (it: WorkspaceItemDTO): boolean => it.type === 'content';

let frontMemo: {
  items: WorkspaceItemDTO[];
  screen: string | null;
  fullscreen: string | null;
  desktop: WorkspaceStoreState['desktopFullscreen'] | undefined;
  result: string;
} | null = null;

/** `<desktop answer>|<handset answer>`, each an item id or empty. Memoised per store version. */
function frontKeySelector(s: WorkspaceStoreState): string {
  if (
    frontMemo &&
    frontMemo.items === s.items &&
    frontMemo.screen === s.currentScreenId &&
    frontMemo.fullscreen === s.fullscreenItemId &&
    frontMemo.desktop === s.desktopFullscreen
  ) {
    return frontMemo.result;
  }
  const base = {
    currentScreenId: s.currentScreenId,
    fullscreenItemId: s.fullscreenItemId,
    desktopFullscreenIds: (s.desktopFullscreen ?? []).map((r) => r.id),
    isApp: isAppWindow,
  };
  const desktop = frontAppItemId(s.items, { ...base, handset: false }) ?? '';
  const handset = frontAppItemId(s.items, { ...base, handset: true }) ?? '';
  const result = `${desktop}|${handset}`;
  frontMemo = { items: s.items, screen: s.currentScreenId, fullscreen: s.fullscreenItemId, desktop: s.desktopFullscreen, result };
  return result;
}

/** The device's answer out of the selector's string. */
export function pickFrontFromKey(key: string, handset: boolean): string | null {
  const [desktop = '', phone = ''] = key.split('|');
  const id = handset ? phone : desktop;
  return id || null;
}

/* ------------------------------------------------------------------ */
/* The component                                                      */
/* ------------------------------------------------------------------ */

export function WebMcpToolsBridge() {
  const bus = useWidgetCommandBus();
  const { user } = useAuth();
  const { anonReadOnly } = useWorkspaceAnon();
  const t = useT();
  const { addItem, bringToFront, setFullscreenItem, setCurrentScreen } = useWorkspaceActions();

  const fullscreenItemId = useFullscreenItemId();
  const fullscreenRef = useRef(fullscreenItemId);
  fullscreenRef.current = fullscreenItemId;

  // `useIsMd` starts false, so without the mounted gate a laptop would answer
  // as a phone for its first frame (the same gate NodeFrame and the bars use).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isMd = useIsMd();
  const handsetLandscape = useHandsetLandscape();
  const handset = mounted && (!isMd || handsetLandscape);

  const frontKey = useWorkspaceState(frontKeySelector);
  const frontItemId = pickFrontFromKey(frontKey, handset);
  const frontRef = useRef(frontItemId);
  frontRef.current = frontItemId;

  const viewerId = user && !isGuestUser(user) ? user.id : null;
  const viewerName = user?.username ?? '';
  const enabled = !!viewerId && !anonReadOnly;
  const assistantName = t('workspace.webmcp.assistantName');

  // Feature detection, after the optional origin trial meta.
  const [modelContext, setModelContext] = useState<ModelContextLike | null>(null);
  useEffect(() => {
    if (!enabled) {
      setModelContext(null);
      return;
    }
    const cfg = getRuntimeConfig();
    if (!cfg.webmcpTools) {
      setModelContext(null);
      return;
    }
    const found = findModelContext();
    if (found) {
      setModelContext(found);
      return;
    }
    const token = cfg.webmcpOriginTrialToken;
    if (!token) {
      setModelContext(null);
      return;
    }
    ensureOriginTrialMeta(document, token);
    let cancelled = false;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = () => {
      if (cancelled) return;
      const mc = findModelContext();
      if (mc) {
        setModelContext(mc);
        return;
      }
      tries += 1;
      if (tries < ORIGIN_TRIAL_POLLS) timer = setTimeout(poll, ORIGIN_TRIAL_POLL_MS);
    };
    timer = setTimeout(poll, 0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled]);

  // The same catalog the Discover panel shows (discover-view.tsx `fetchRoot`),
  // through its own cache: app listings under the `apps` root, never agents,
  // and the id handed back is the content id `addItem` mounts.
  const findApps = useCallback(async (query: string, limit: number): Promise<CatalogApp[]> => {
    const params = new URLSearchParams({
      root: 'apps',
      limit: String(Math.min(MAX_FIND_APPS, Math.max(1, limit))),
      offset: '0',
    });
    if (query) params.set('q', query);
    const data = await cachedJson<{ items?: unknown }>(`/api/marketplace/channels?${params.toString()}`);
    const items = Array.isArray(data?.items) ? (data.items as Partial<CatalogListing>[]) : [];
    const out: CatalogApp[] = [];
    for (const l of items) {
      if (!l || typeof l !== 'object') continue;
      if (l.primaryAgentId || typeof l.primaryContentId !== 'string' || !l.primaryContentId) continue;
      out.push({
        id: l.primaryContentId,
        title: typeof l.name === 'string' ? l.name : '',
        description: typeof l.description === 'string' ? l.description : '',
        maker: typeof l.makerUsername === 'string' ? l.makerUsername : null,
      });
    }
    return out;
  }, []);

  // The store's own `addItem` with `openedBy: 'agent'`, the call the hosted
  // `open_app` tool ends in (agent-command-receiver.tsx): no x/y, so the
  // window lands on the screen the person is on, with the spawn highlight
  // and without taking the keyboard. The cache is the first-paint name; the
  // resolve-ref round trip fills in the rest.
  const openApp = useCallback(
    async (app: CatalogApp): Promise<{ itemId: string }> => {
      const item = await addItem({
        type: 'content',
        refId: app.id,
        openedBy: 'agent',
        state: { cache: { displayName: app.title, contentType: 'widget' } },
      });
      return { itemId: item.id };
    },
    [addItem],
  );

  // Bring an open window to the front the way the app switcher does: under
  // the fullscreen takeover, swap the takeover in place and raise the window;
  // on the canvas, the `ws:screen` chokepoint frames it, raises it and
  // un-docks it if it was minimised.
  const focusApp = useCallback(
    (itemId: string) => {
      if (fullscreenRef.current) {
        setFullscreenItem(itemId);
        bringToFront(itemId);
        setCurrentScreen(itemId);
        return;
      }
      window.dispatchEvent(new CustomEvent('ws:screen', { detail: { kind: 'item', itemId } }));
    },
    [bringToFront, setCurrentScreen, setFullscreenItem],
  );

  // The page's copy of the consent record, one gate per viewer
  // (lib/workspaces/webmcp-consent-gate.ts). A ref, not state: the registrar
  // asks it from inside a tool call, and it has to outlive the registrar,
  // which is rebuilt whenever one of its closures changes.
  const gateRef = useRef<{ forUser: string; gate: WebMcpConsentGate } | null>(null);
  const gateFor = useCallback((userId: string): WebMcpConsentGate => {
    const held = gateRef.current;
    if (held && held.forUser === userId) return held.gate;
    const gate = new WebMcpConsentGate({
      read: readWebMcpConsentRecord,
      save: saveWebMcpConsentAllow,
      askPerson: askWebMcpConsent,
    });
    gateRef.current = { forUser: userId, gate };
    return gate;
  }, []);

  const registrarRef = useRef<WebMcpRegistrar | null>(null);
  useEffect(() => {
    if (!enabled || !bus || !modelContext || !viewerId) return;
    const gate = gateFor(viewerId);
    // Read ahead, so a call that comes soon does not wait for it.
    void gate.refresh();
    // Settings removed the permission in this browser: honour it now (S-382).
    const consentChannel = openWebMcpConsentChannel(() => gate.drop());
    const registrar = new WebMcpRegistrar({
      modelContext,
      bus,
      getFrontItemId: () => frontRef.current,
      actor: { kind: 'browser', id: `webmcp:${viewerId}`, name: assistantName, ownerName: viewerName },
      userId: viewerId,
      consent: { isAllowed: () => gate.isAllowed(), ask: () => gate.ask() },
      findApps,
      openApp,
      focusApp,
      onError: (toolName, err) => {
        console.warn(`[webmcp] the browser refused tool ${toolName}: ${(err as { message?: string } | null)?.message ?? String(err)}`);
      },
    });
    registrarRef.current = registrar;
    registrar.start();
    return () => {
      consentChannel?.close();
      registrar.dispose();
      if (registrarRef.current === registrar) registrarRef.current = null;
    };
  }, [enabled, bus, modelContext, viewerId, viewerName, assistantName, gateFor, findApps, openApp, focusApp]);

  // A different window in front: swap the `app.*` tools (coalesced with any
  // bus change that caused it).
  useEffect(() => {
    registrarRef.current?.scheduleSync();
  }, [frontItemId]);

  return null;
}
